import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Stack,
  Pagination,
  Paper,
  useTheme,
} from "@mui/material";
import { tokens } from "../theme";
import SendIcon from "@mui/icons-material/Send";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import AppButton from "../components/buttons/AppButton";
import SendManualSmsModal from "../components/sms/SendManualSmsModal";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getOutbox,
  resetSmsOutboxServerStatus,
} from "../redux/actions/smsOutboxActions";
import { getBranchSettings } from "../redux/actions/settingsActions";
import { SmsStatus } from "../shared/enums/SmsStatus";

const SmsOutboxPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const {
    items: outbox,
    pagination,
    loading: loadingOutbox,
    error: errorLoadingOutbox,
    serverStatus,
    loadingSendManual: loadingSendManualSms,
    errorSendManual: errorSendingManualSms,
  } = useAppSelector((state) => state.smsOutbox);
  const { branchSettings } = useAppSelector((state) => state.settings);

  const [page, setPage] = useState<number>(1);
  const [isSendModalOpen, setIsSendModalOpen] = useState<boolean>(false);

  const canSendPerm = hasPermission("sms_outbox:create");
  const isSmsIntegrated = Boolean(
    branchSettings?.communicationIntegration?.active &&
    branchSettings?.communicationIntegration?.allowsSms,
  );

  const canSend = canSendPerm && isSmsIntegrated;

  useEffect(() => {
    dispatch(getOutbox({ page, size: 20 }));
    if (!branchSettings) dispatch(getBranchSettings());
  }, [branchSettings, dispatch, page]);

  const handleCloseSend = useCallback(() => {
    if (!loadingSendManualSms) setIsSendModalOpen(false);
  }, [loadingSendManualSms]);

  useEffect(() => {
    if (!serverStatus) return;
    if (serverStatus.operation === "create" && serverStatus.status === 201) {
      handleCloseSend();
      dispatch(getOutbox({ page: 1, size: 20 }));
      setPage(1);
    }
    dispatch(resetSmsOutboxServerStatus());
  }, [serverStatus, dispatch, handleCloseSend]);

  const renderStatusIcon = (status: SmsStatus): React.ReactNode => {
    if (status === "DELIVERED" || status === "SENT")
      return <DoneAllIcon fontSize="small" color="info" />;
    if (status === "FAILED")
      return <ErrorOutlineIcon fontSize="small" color="error" />;
    return <AccessTimeIcon fontSize="small" sx={{ color: "text.disabled" }} />;
  };

  const renderContent = (): React.JSX.Element => {
    if (loadingOutbox) return <AppCircularProgress />;
    if (errorLoadingOutbox)
      return (
        <PageError
          error={errorLoadingOutbox}
          onRetry={() => dispatch(getOutbox())}
        />
      );
    if (!outbox || outbox.length === 0)
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography sx={{ color: "text.secondary" }}>
            No Outbound Messages Found
          </Typography>
        </Box>
      );

    return (
      <Box
        sx={{
          maxWidth: 800,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {outbox.map((msg) => (
          <Box
            key={msg.id}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Paper
              elevation={1}
              sx={{
                p: 2,
                minWidth: 250,
                maxWidth: "80%",
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? colors.primary[300]
                    : colors.grey[300]
                }`,
                borderRadius: 2,
                borderTopRightRadius: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  color: "text.secondary",
                  display: "block",
                  mb: 0.5,
                }}
              >
                To: {msg.phoneNumber}
              </Typography>
              <Typography
                variant="body1"
                sx={{ whiteSpace: "pre-wrap", mb: 1 }}
              >
                {msg.message}
              </Typography>
              <Stack
                sx={{
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  {new Date(msg.createdAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
                {renderStatusIcon(msg.status)}
              </Stack>
            </Paper>
          </Box>
        ))}

        {pagination && pagination.totalPages! > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_e, value) => setPage(value)}
              color="primary"
            />
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <PageContainer
      title="SMS Outbox"
      subtitle="View sent messages and queue manual SMS."
      actions={
        canSend && (
          <AppButton
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setIsSendModalOpen(true)}
          >
            New SMS
          </AppButton>
        )
      }
    >
      <Box
        sx={{
          bgcolor: "background.default",
          p: { xs: 2, md: 4 },
          borderRadius: 2,
          minHeight: "100%",
          border: `1px solid ${
            theme.palette.mode === "dark"
              ? colors.primary[300]
              : colors.grey[300]
          }`,
        }}
      >
        {renderContent()}
      </Box>

      <SendManualSmsModal
        open={isSendModalOpen}
        onClose={handleCloseSend}
        loading={loadingSendManualSms}
        error={errorSendingManualSms}
      />
    </PageContainer>
  );
};

export default SmsOutboxPage;
